/**
 * This hook determines whether the user should be nudged to sign up for MyLeads.
 * It combines the LaunchDarkly feature flag `shouldNudgeToSignUp` and
 * the store value `$shouldNudgeToSignUpStore` to calculate the result.
 *
 * @returns {object} - Object containing `shouldNudgeToSignUp`.
 *
 * Usage:
 * const { shouldNudgeToSignUp } = useMyLeadsBoostFeature()
 */

import { useStore } from "@nanostores/react"
import {
  $propertiesWidgetViewCount,
  $isBuyBoxSetUp,
  setIsMyLeadsRunning,
} from "@/stores/myLeads"
import { useBoolVariation, useStringVariation } from "@/hooks/launchDarklyHooks"
import { router } from "expo-router"
import { useGoogleLoginGmailMutation, useUser } from "@/queries/user"
import { MYLEADS_GOOGLE_SCOPES } from "@/constants/Scopes"
import { useAnalyses } from "@/queries/analysis"
import { useWindowDimensions } from "react-native"

export const useMyLeadsBoostFeature = (onDismiss?: () => void) => {
  const { data: user } = useUser()
  const { data: analyses } = useAnalyses("updated_at", null, false)
  const { height: screenHeight } = useWindowDimensions()

  const heightOverlapThreshold = 822
  const screenHeightCropped = screenHeight < heightOverlapThreshold // determines if the screen/device is short on space to fit content

  const hasAnalyses = (analyses?.pages?.[0]?.analyses?.length ?? 0) > 0

  const widgetViewCount = useStore($propertiesWidgetViewCount)
  const isBuyBoxSetUp = useStore($isBuyBoxSetUp)

  // Feature flag from LaunchDarkly
  const featureIsEnabled = useBoolVariation("my-leads-boost", false)

  type Variation = "Control" | "treatment_1" | "treatment_2"
  const variation: Variation = useStringVariation(
    "my-leads-carousel-experiment",
    "Control"
  ) as Variation
  // const variation: Variation = "treatment_1"

  const myLeadsEnabled = !!(
    user?.me?.googleToken && !user.me.googleToken.invalidAt
  )

  const shouldNudgeToSignUp =
    featureIsEnabled && !myLeadsEnabled && widgetViewCount <= 3

  const shouldNudgeBuyBoxSetup =
    featureIsEnabled && myLeadsEnabled && !isBuyBoxSetUp && !shouldNudgeToSignUp

  const googleLoginGmailMutation = useGoogleLoginGmailMutation()

  // console.log(`*variation- ${variation}`)

  // Reusable onSuccess logic
  const handleMyLeadsConnectionSuccess = (
    code: string | null,
    redirectUri?: string
  ) => {
    if (code) {
      googleLoginGmailMutation.mutate(
        {
          serviceType: "gmail",
          code,
          scopes: MYLEADS_GOOGLE_SCOPES,
          redirectUri,
        },
        {
          onSuccess: (data) => {
            if (data?.googleLogin?.token) {
              setIsMyLeadsRunning(true)
              if (onDismiss) {
                onDismiss()
              } else {
                console.log("Dismissing modal and redirecting")
                router.push("/properties/myleads/success")
              }
            }
          },
          onError: (error: Error) => {
            console.error("Error during MyLeads connection:", error)
          },
        }
      )
    }
  }

  const isCarouselView =
    !myLeadsEnabled &&
    !hasAnalyses &&
    featureIsEnabled &&
    (variation === "treatment_1" || variation === "treatment_2")

  const hasBuyBox = !!user?.me?.buyBox

  return {
    hasAnalyses,
    myLeadsEnabled,
    featureIsEnabled,
    variation,
    shouldNudgeToSignUp,
    shouldNudgeBuyBoxSetup,
    handleMyLeadsConnectionSuccess,
    isCarouselView,
    isBuyBoxSetUp,
    hasBuyBox,
    screenHeightCropped,
  }
}
